import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Who is Sam Ortega?",
  "What is the Ecentra opportunity?",
  "How do I start my wellness journey?",
];

const WELCOME = {
  role: "assistant",
  content:
    "Welcome. I am the concierge of Sam Ortega's legacy page. Ask me anything about Sam's 30-year journey, the Ecentra opportunity across Malaysia, the Philippines and Singapore — or how to begin your own run.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  const sessionRef = useRef(null);
  if (!sessionRef.current && typeof window !== "undefined") {
    let sid = localStorage.getItem("sam_chat_session");
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem("sam_chat_session", sid);
    }
    sessionRef.current = sid;
  }

  useEffect(() => {
    if (!open || !sessionRef.current) return;
    fetch(`${API}/chat/${sessionRef.current}/history`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.messages?.length) setMessages([WELCOME, ...d.messages]);
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const appendDelta = (delta) =>
    setMessages((m) => {
      const c = [...m];
      c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + delta };
      return c;
    });

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("chat request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) appendDelta(delta);
          } catch { /* partial chunk */ }
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = {
          role: "assistant",
          content: "A momentary connection issue — please try again, or leave your details in the Direct Connect form below.",
        };
        return c;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2.2, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle-button"
        aria-label="Open AI concierge chat"
        className="fixed bottom-5 left-5 z-[80] w-14 h-14 rounded-full bg-gold text-[#0A0A0B] flex items-center justify-center shadow-[0_8px_40px_rgba(212,175,55,0.35)] hover:bg-gold-champagne hover:scale-105 active:scale-95 transition-all duration-300"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 left-5 z-[80] w-[calc(100vw-2.5rem)] max-w-md h-[560px] max-h-[70vh] flex flex-col bg-[rgba(15,15,18,0.97)] backdrop-blur-xl border border-[rgba(212,175,55,0.35)] shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
            data-testid="chat-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[rgba(212,175,55,0.2)]">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 border border-[rgba(212,175,55,0.5)] flex items-center justify-center">
                  <Sparkles size={16} className="text-gold" />
                </span>
                <div className="leading-tight">
                  <p className="font-serif-lux text-base text-[#F7F7F8]">Sam's AI Concierge</p>
                  <p className="font-mono-lux text-[9px] tracking-[0.25em] text-gold">POWERED BY GPT-5.4</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="chat-close-button"
                aria-label="Close chat"
                className="text-[#757582] hover:text-gold-champagne transition-colors p-1"
              >
                <X size={18} />
              </button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${i}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-gold text-[#0A0A0B] font-medium"
                        : "bg-[#17171C] border border-[rgba(212,175,55,0.18)] text-[#D8D8DF]"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? <span className="inline-flex gap-1 py-1"><span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" /><span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse [animation-delay:150ms]" /><span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse [animation-delay:300ms]" /></span> : "")}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`chat-suggestion-${i}`}
                      className="text-xs text-gold-champagne border border-[rgba(212,175,55,0.35)] px-3.5 py-2 hover:bg-[rgba(212,175,55,0.1)] hover:border-[rgba(243,227,166,0.6)] transition-colors duration-300"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 px-4 py-3.5 border-t border-[rgba(212,175,55,0.2)]"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about Sam, the story, or Ecentra..."
                data-testid="chat-input"
                className="flex-1 bg-[#0A0A0C] border border-[rgba(212,175,55,0.22)] text-sm text-[#F7F7F8] px-4 py-3 placeholder:text-[#55555f] focus:outline-none focus:border-gold transition-colors duration-300"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Send message"
                className="w-11 h-11 shrink-0 bg-gold text-[#0A0A0B] flex items-center justify-center hover:bg-gold-champagne disabled:opacity-40 transition-colors duration-300 active:scale-95"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
